import { StyleSheet, css } from 'aphrodite';
import { Avatar } from '../../../web/components-v2/Avatar';
import { Button } from '../../../web/components-v2/Button';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { accessibilityStyles } from '../../../web/styles/styles';
import { ITimeEntry } from '../../queries/TimeEntry';
import { LoadError } from '../../timeTracking/components/LoadError';
import { SubmitNote } from '../../timeTracking/components/SubmitNote';
import { TimeEntryStatusPill } from '../../timeTracking/components/TimeEntryStatusPill';
import { formatDateTime, formatDayShort } from '../../timeTracking/days';
import { formatHours } from '../../timeTracking/hours';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';

export function AllEntriesTable({
	entries,
	isLoading,
	isError,
	onRetry,
	nameFor,
	initialsFor,
	onOpenRep,
}: {
	entries: ITimeEntry[];
	isLoading: boolean;
	isError: boolean;
	onRetry: () => void;
	nameFor: (userId?: string) => string;
	initialsFor: (userId?: string) => string;
	onOpenRep: (userId: string) => void;
}) {
	return (
		<section className={css(tt.card, tt.tableCard)} aria-label='All hours'>
			<DataGrid>
				<DataGridColumns>
					<DataGridColumn sticky>Rep</DataGridColumn>
					<DataGridColumn sticky>Day</DataGridColumn>
					<DataGridColumn sticky>Hours</DataGridColumn>
					<DataGridColumn sticky>Status</DataGridColumn>
					<DataGridColumn sticky>Last change</DataGridColumn>
					<DataGridColumn sticky>
						<span className={css(accessibilityStyles.srOnly)}>Actions</span>
					</DataGridColumn>
				</DataGridColumns>
				<DataGridRows>
					{isLoading ? (
						<DataGridRow>
							<DataGridRowItem colSpan={6}>
								<LoadingSpinner type='large' />
							</DataGridRowItem>
						</DataGridRow>
					) : isError ? (
						<DataGridRow>
							<DataGridRowItem colSpan={6}>
								<LoadError message='We couldn’t load these hours.' onRetry={onRetry} />
							</DataGridRowItem>
						</DataGridRow>
					) : entries.length ? (
						entries.map(entry => (
							<DataGridRow key={entry.id}>
								<DataGridRowItem>
									<span className={css(styles.rep)}>
										<Avatar
											fallbackText={initialsFor(entry.userId)}
											altText={nameFor(entry.userId)}
											smallText
											styles={[styles.avatar]}
										/>
										<span className={css(tt.truncate)}>{nameFor(entry.userId)}</span>
									</span>
								</DataGridRowItem>
								<DataGridRowItem>{formatDayShort(entry.day)}</DataGridRowItem>
								<DataGridRowItem>
									<span className={css(styles.hours)}>
										<span className={css(tt.number)}>{formatHours(entry.hours)}</span>
										<SubmitNote note={entry.submitNote} />
									</span>
								</DataGridRowItem>
								<DataGridRowItem>
									<TimeEntryStatusPill entry={entry} />
								</DataGridRowItem>
								<DataGridRowItem styles={[styles.sub]}>{formatDateTime(entry.lastChangedAt)}</DataGridRowItem>
								<DataGridRowItem styles={[tt.actionCell]}>
									<Button type='button' variant='outline' size='sm' onClick={() => onOpenRep(entry.userId)}>
										View rep
									</Button>
								</DataGridRowItem>
							</DataGridRow>
						))
					) : (
						<DataGridRow>
							<DataGridRowItem colSpan={6} styles={[tt.emptyCell]}>
								No hours match these filters.
							</DataGridRowItem>
						</DataGridRow>
					)}
				</DataGridRows>
			</DataGrid>
		</section>
	);
}

const styles = StyleSheet.create({
	avatar: {
		flexShrink: 0,
		height: 32,
		width: 32,
	},
	hours: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
	},
	rep: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
		minWidth: 0,
	},
	sub: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
	},
});
